import "./documentRow.scss";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import newRequest from "../../utils/newRequest";
import { ScaleLoader } from "react-spinners";
import { GoHomeFill } from "react-icons/go";
import { qilloIconMap } from "../../utils/iconMapping";
import { MdArrowCircleRight, MdOutlineNumbers } from "react-icons/md";
import { CustomButtonSmall } from "../ui/CustomButton";
import { CiImageOn } from "react-icons/ci";
import { FaReceipt } from "react-icons/fa";
import { RiBuilding2Fill } from "react-icons/ri";
import { GrTransaction } from "react-icons/gr";
import { PiPackageDuotone } from "react-icons/pi";
import { BsCalendar2Date } from "react-icons/bs";

const ReceiptsWidget = () => {
    const { t } = useTranslation();
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const [receipts, setReceipts] = useState([]);

    const fetchReceipts = async () => {
        try {
            setIsLoading(true);
            await newRequest
                .post("/arReceiptHdr/findAll", {
                    hd_mode: "",
                    hd_status: "",
                    hd_paying_for: "",
                    result_limit: 3,
                })
                .then((res) => {
                    setReceipts(res.data);
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setIsLoading(false);
                });
        } catch (err) {
            console.log(err);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchReceipts();
    }, [currentUser]);

    return (
        <motion.div
            className="document-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 1 } }}
            exit={{ opacity: 0, transition: { duration: 1 } }}
        >
            {isLoading ? (
                <div className="loading-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                receipts?.length > 0 ?
                    <div className="policies-wrapper">
                        {receipts?.map((row) => {
                            const IconComponent = FaReceipt;

                            return (
                                <div key={row?._id} className="rnCard receiptCard">
                                    {/* Top row */}
                                    <div className="rnTop">
                                        <div className="rnProduct">
                                            <div className="rnProductIcon">
                                                <IconComponent className="product-icon" />
                                            </div>
                                            <div className="rnProductMeta">
                                                <div className="rnProductName">{row?.hd_no}</div>
                                            </div>
                                        </div>

                                        <div className="rnTopRight">
                                            <span className={`status ${row?.hd_status}`}>{`${t("status")} : ${row?.hd_status}`}</span>

                                            <span className="rnNoteNo">{`${t("type")} : ${row?.hd_type || "Normal"}`}</span>

                                            <span className="rnDate">
                                                <BsCalendar2Date className="title-icon" />
                                                {row?.formatted_gldate_xx}
                                            </span>

                                            <CustomButtonSmall
                                                currentTheme={currentTheme}
                                                iconPosition="end"
                                                endIcon={<MdArrowCircleRight size={20} />}
                                                onClick={() => {
                                                    try {
                                                        navigate(`/receipt/${row._id}`, { state: row });
                                                    } catch (error) {
                                                        console.log(error);
                                                    }
                                                }}
                                            >
                                                {t("view")}
                                            </CustomButtonSmall>
                                        </div>
                                    </div>

                                    {/* Bottom row */}
                                    <div className="rnBottom receiptBottom">
                                        {/* Client */}
                                        <div className="rnCol">
                                            <div className="rnEntity">
                                                <div className="rnAvatar">
                                                    {row?.client_details_xx?.ent_photo_url ? (
                                                        <img src={row?.client_details_xx?.ent_photo_url} alt="" />
                                                    ) : (
                                                        <CiImageOn className="rnAvatarIcon" />
                                                    )}
                                                </div>

                                                <div className="rnEntityMeta">
                                                    <div className="rnEntityName">{row?.client_details_xx?.ent_name || ""}</div>
                                                    <div className="rnEntitySub">{row?.client_details_xx?.aent_name_xx}</div>
                                                </div>
                                            </div>

                                            <div className="rnMetaLine">
                                                <MdOutlineNumbers className="rnMetaIcon" />
                                                <span className="rnMetaText">{row?.client_details_xx?.ent_code}</span>
                                            </div>
                                        </div>

                                        {/* Details */}
                                        <div className="rnCol">
                                            <div className="rnMetaLine">
                                                <GrTransaction className="rnMetaIcon" />
                                                <span className="rnMetaText">{`${t("mode")} : ${row?.receipt_mode_xx || ""}`}</span>
                                            </div>

                                            <div className="rnMetaLine">
                                                <RiBuilding2Fill className="rnMetaIcon" />
                                                <span className="rnMetaText">{`${t("bizUnit")} : ${row?.bizunit_name_xx || ""}`}</span>
                                            </div>

                                            <div className="rnMetaLine">
                                                <PiPackageDuotone className="rnMetaIcon" />
                                                <span className="rnMetaText">{`${t("payingFor")} : ${row?.hd_paying_for || ""}`}</span>
                                            </div>
                                        </div>

                                        {/* Amounts */}
                                        <div className="rnCol rnLimits">
                                            <div className="rnLabel">{`${t("currency").toUpperCase()} - [${row?.hd_cur_code}]`}</div>

                                            <div className="rnAmount">
                                                <span className="rnAmountLabel">{t("received")}</span>
                                                <span className="rnAmountValue">{row?.fmt_fc_amount}</span>
                                            </div>

                                            <div className="rnAmount">
                                                <span className="rnAmountLabel">{t("allocated")}</span>
                                                <span className="rnAmountValue">{row?.fmt_fc_allocated}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    :
                    <div className="no-content-wrapper">
                        <span className="main-title">Your last three receipts will appear here once you create them</span>
                    </div>
            )}
        </motion.div>
    );
};

export default ReceiptsWidget